/**
 * WebAuthn (biometric) helper — iter-86.
 *
 * Browser API wrapper. The browser expects `ArrayBuffer` for challenge,
 * user.id, and allowCredentials[].id, but our backend serializes those as
 * base64url strings. This module handles the round-trip so the calling
 * component only sees JSON.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -------- base64url helpers -------------------------------------------------
function b64uToBuffer(b64u) {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

function bufferToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function biometricsAvailable() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!window.navigator?.credentials?.create;
}

// -------- Registration ------------------------------------------------------
/** Enroll the current device as a biometric login for the signed-in user. */
export async function enrollBiometric(nickname = 'This device') {
  const beginRes = await fetch(`${BACKEND_URL}/api/webauthn/register/begin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!beginRes.ok) { const e = await beginRes.json().catch(() => ({})); throw new Error(e.detail || 'Could not start biometric enrollment.'); }
  const opts = await beginRes.json();

  const publicKey = {
    ...opts,
    challenge: b64uToBuffer(opts.challenge),
    user: { ...opts.user, id: b64uToBuffer(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64uToBuffer(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Enrollment cancelled.');

  const payload = {
    credential: {
      id: cred.id,
      rawId: bufferToB64u(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufferToB64u(cred.response.clientDataJSON),
        attestationObject: bufferToB64u(cred.response.attestationObject),
        transports: cred.response.getTransports ? cred.response.getTransports() : [],
      },
      clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    },
    nickname,
  };
  const completeRes = await fetch(`${BACKEND_URL}/api/webauthn/register/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.detail || 'Enrollment verification failed.');
  }
  return completeRes.json();
}

// -------- Login -------------------------------------------------------------
/** Prompt the biometric sensor and sign the user in. */
export async function loginWithBiometric(email = '') {
  const beginRes = await fetch(`${BACKEND_URL}/api/webauthn/login/begin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email || null }),
  });
  if (!beginRes.ok) throw new Error('Biometric login not available for this account.');
  const opts = await beginRes.json();
  const key = opts.__key;

  const publicKey = {
    ...opts,
    challenge: b64uToBuffer(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64uToBuffer(c.id),
    })),
  };
  delete publicKey.__key;

  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error('Biometric prompt cancelled.');

  const payload = {
    key,
    credential: {
      id: assertion.id,
      rawId: bufferToB64u(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToB64u(assertion.response.clientDataJSON),
        authenticatorData: bufferToB64u(assertion.response.authenticatorData),
        signature: bufferToB64u(assertion.response.signature),
        userHandle: assertion.response.userHandle ? bufferToB64u(assertion.response.userHandle) : null,
      },
      clientExtensionResults: assertion.getClientExtensionResults ? assertion.getClientExtensionResults() : {},
    },
  };
  const completeRes = await fetch(`${BACKEND_URL}/api/webauthn/login/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!completeRes.ok) {
    const err = await completeRes.json().catch(() => ({}));
    throw new Error(err.detail || 'Biometric verification failed.');
  }
  return completeRes.json();
}

export async function listBiometricCredentials() {
  const res = await fetch(`${BACKEND_URL}/api/webauthn/credentials`, { credentials: 'include' });
  if (!res.ok) throw new Error('Unable to load biometric login status.');
  return res.json();
}

export async function removeBiometricCredential(credId) {
  const res = await fetch(`${BACKEND_URL}/api/webauthn/credentials/${encodeURIComponent(credId)}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!res.ok) throw new Error('Could not remove authenticator.');
  return res.json();
}

export async function disableBiometricLogin() {
  const res = await fetch(`${BACKEND_URL}/api/webauthn/credentials`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Could not disable biometric login.'); }
  return res.json();
}
